'use client'

import Link from 'next/link'
import { Suspense, useEffect, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { useBlockPageSidebar } from '~/components/BlockPageLayoutClient'
import { CompletionTagSkeleton } from '~/components/blocks/BlockSkeleton'
import CompletionTag from '~/components/blocks/CompletionTag'
import CourseProgressIndicator from '~/components/CourseProgressIndicator'
import FocusModeToggleButton from '~/components/FocusModeToggleButton'
import { CourseModule } from '~/payload-types'
import { SupportedLocales } from '~/types'

export default function BlockPageMobileSidebar({
  locale,
  courseId,
  moduleId,
  blockIdent,
  currentModule,
}: {
  locale: string
  courseId: string
  moduleId: string
  blockIdent: string
  currentModule: CourseModule
}) {
  const { isMobileOpen: isOpen, setMobileOpen: setIsOpen } = useBlockPageSidebar()
  const [topOffset, setTopOffset] = useState(56)

  useEffect(() => {
    let raf = 0
    const updateTopOffset = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        const headerBottom = document.querySelector('header')?.getBoundingClientRect().bottom ?? 0
        setTopOffset(Math.max(0, headerBottom))
      })
    }

    updateTopOffset()
    window.addEventListener('scroll', updateTopOffset, { passive: true })
    window.addEventListener('resize', updateTopOffset, { passive: true })

    const observer = new MutationObserver(updateTopOffset)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-focus-mode', 'data-focus-header'],
    })

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('scroll', updateTopOffset)
      window.removeEventListener('resize', updateTopOffset)
      observer.disconnect()
    }
  }, [])

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  const basePath = `/${locale}/course/${courseId}/module/${moduleId}`

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-x-0 bottom-0 z-40 lg:hidden bg-black/50"
          style={{ top: `${topOffset}px` }}
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Mobile sidebar */}
      <nav
        className={`lg:hidden fixed right-0 bottom-0 w-80 max-w-[85vw] bg-white shadow-xl z-40 transform transition-transform duration-300 ease-in-out overflow-y-auto ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ top: `${topOffset}px` }}
      >
        <div className="p-6 space-y-6 text-sm">
          <Suspense fallback={<div className="h-12 mb-4 bg-gray-100 rounded animate-pulse" />}>
            <CourseProgressIndicator courseId={courseId} locale={locale as SupportedLocales} />
          </Suspense>
          <div className="mb-4">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                Navigation
              </h2>
              <FocusModeToggleButton className="h-7 w-7" />
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-base font-semibold text-gray-900">{currentModule.title}</h3>

            {currentModule.units && currentModule.units.length > 0 && (
              <div className="space-y-6">
                {currentModule.units.map((modUnit) => {
                  if (typeof modUnit === 'number') return null
                  const unitPath = basePath + `/unit/${modUnit.id}`
                  return (
                    <div key={modUnit.id} className="space-y-2">
                      <Link
                        href={unitPath}
                        onClick={() => setIsOpen(false)}
                        className="font-medium text-gray-900 hover:text-primary-80"
                      >
                        {modUnit.title}
                      </Link>

                      <div className="ml-6 space-y-1.5">
                        {modUnit.components.map((block) => {
                          const href = unitPath + `/block/${block.identifier}`
                          const isActive = block.identifier === blockIdent
                          return (
                            <div key={block.id} className="flex items-start justify-between gap-2">
                              <Link
                                href={href}
                                onClick={() => setIsOpen(false)}
                                className={twMerge(
                                  'flex-1 min-w-0 hover:text-primary-80',
                                  isActive ? 'text-primary-100 font-medium' : 'text-gray-700',
                                )}
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <span className="flex-1 text-sm">{block.title}</span>
                                  <span className="text-xs text-gray-500 whitespace-nowrap">
                                    {block.time} min
                                  </span>
                                </div>
                              </Link>
                              <div className="flex-shrink-0">
                                <Suspense fallback={<CompletionTagSkeleton />}>
                                  <CompletionTag
                                    unitId={modUnit.id}
                                    moduleId={Number(moduleId)}
                                    courseId={Number(courseId)}
                                    blockIdentifier={block.identifier}
                                    blockTime={block.time}
                                    variant="icon"
                                  />
                                </Suspense>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </nav>
    </>
  )
}
